import { useTranslation } from 'react-i18next';

import StatusDescriptionModal from 'components/stateless/Modal/common/status/StatusDescriptionModal';
import Pagination from 'components/stateless/Pagination/Pagination';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import TdLink from 'components/stateless/Table/TdLink';
import Tr from 'components/stateless/Table/Tr';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import { MULTIPLE_LOAN_APPROVAL_TYPE, MULTIPLE_LOAN_REQUEST_STATUS } from 'enums';
import type { PageableType } from 'hooks/usePageable';
import type { MultipleLoanRequestVOModel } from 'models/vo/MultipleLoanRequestVO';
import getStatusTextClass from 'utils/classNames/getStatusTextClass';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';

interface FinancierBulkFinancingApprovalListTableProps {
  bulkFinancingConfirmationListData: MultipleLoanRequestVOModel[] | undefined;
  pageable: PageableType;
  paginate: (pageNumber: number, sizePerPage: number) => Promise<void>;
}

function FinancierBulkFinancingApprovalListTable({
  bulkFinancingConfirmationListData,
  pageable,
  paginate,
}: FinancierBulkFinancingApprovalListTableProps) {
  const { t } = useTranslation(['format']);
  const modal = useModal();

  const handleMultipleLoanRequestStatusQuestionButtonClick = (): void => {
    modal.show(
      <StatusDescriptionModal
        statusDescriptionEnum={[
          MULTIPLE_LOAN_REQUEST_STATUS.APPLIED,
          MULTIPLE_LOAN_REQUEST_STATUS.CANCELLED,
          MULTIPLE_LOAN_REQUEST_STATUS.REJECTED,
          MULTIPLE_LOAN_REQUEST_STATUS.APPROVED,
        ]}
        statusDescriptionModalType="TEXT"
        statusDescriptionEnumType="MULTIPLE_LOAN_REQUEST_STATUS"
      />,
      {
        modalSize: ModalSize.XL,
        modalType: ModalType.ALERT,
        closeBtnText: t('text:Close'),
      },
    );
  };

  const handleApprovalStatusQuestionButtonClick = () => {
    modal.show(
      <StatusDescriptionModal
        statusDescriptionEnum={Object.values([
          MULTIPLE_LOAN_APPROVAL_TYPE.OPERATOR_REQUEST,
          MULTIPLE_LOAN_APPROVAL_TYPE.AUTHORIZER_RETURNED,
          MULTIPLE_LOAN_APPROVAL_TYPE.OPERATOR_REQUEST_REJECTED,
          MULTIPLE_LOAN_APPROVAL_TYPE.AUTHORIZER_RETURNED_REJECTED,
        ])}
        statusDescriptionModalType="TEXT"
        statusDescriptionEnumType="MULTIPLE_LOAN_APPROVAL_TYPE"
      />,
      {
        modalSize: ModalSize.XL,
        closeBtnText: t('text:Close'),
      },
    );
  };

  const BULK_CONFIRMATION_TABLE_HEADERS: HeaderType[] = [
    {
      headerText: t('text:Partner_Name'),
    },
    {
      headerText: t('text:Associated_Anchor_Name'),
    },
    {
      headerText: t('text:Associated_Anchor_Master_Agreement'),
    },
    {
      headerText: t('text:Partner_Master_Agreement_Number'),
    },
    {
      headerText: t('text:Currency'),
    },
    {
      headerText: t('text:Total_Amount_of_AR'),
    },
    {
      headerText: t('text:Total_Requested_Financing_Amount'),
    },
    {
      headerText: t('text:Discount_Rate_APR'),
    },
    {
      headerText: t('text:Total_Number_of_Requested_AR'),
    },
    {
      headerText: t('text:Bulk_Financing_Status'),
      hasStatusDescription: true,
      showStatusDescriptionFunc: handleMultipleLoanRequestStatusQuestionButtonClick,
    },
    {
      headerText: t('text:Bulk_Approval_Status'),
      hasStatusDescription: true,
      showStatusDescriptionFunc: handleApprovalStatusQuestionButtonClick,
    },
    {
      headerText: '',
      colWidths: 50,
    },
  ];

  return (
    <div className="content-area">
      <SectionTitle title={t('text:Select_Financing')} />
      <TableBorder>
        <TableHeader header={BULK_CONFIRMATION_TABLE_HEADERS} />
        <TableBody numOfCol={BULK_CONFIRMATION_TABLE_HEADERS.length}>
          {bulkFinancingConfirmationListData?.map((item: MultipleLoanRequestVOModel, index: number) => (
            <Tr key={index}>
              <Td data={item.dealerClientName} />
              <Td data={item.anchorClientName} />
              <Td data={item.anchorAgreementContractNo} />
              <Td data={item.dealerAgreementContractNo} />
              <Td data={item.currencyType} />
              <Td data={item.totalArAmount} format="number" />
              <Td data={item.totalRequestAmount} format="number" />
              <Td data={item.averageInterestRate} format="percent" />
              <Td data={item.totalArCount} />
              <Td
                data={t(`code:multiple-loan-request-status.${item.multipleLoanRequestStatus}`)}
                className={getStatusTextClass('MULTIPLE_LOAN_REQUEST_STATUS', item.multipleLoanRequestStatus)}
                format="code"
              />
              <Td
                data={t(`code:multiple-loan-approval-type.${item.approvalType}`)}
                className={getStatusTextClass('MULTIPLE_LOAN_APPROVAL_TYPE', item.approvalType)}
                format="code"
              />
              <TdLink path={ROUTES_FI.MANAGE_FINANCING.BULK_APPROVAL_DETAIL_BUILD_PATH(item.multipleLoanRequestId)} />
            </Tr>
          ))}
        </TableBody>
      </TableBorder>
      <Pagination pageable={pageable} paginate={paginate} />
    </div>
  );
}

export default FinancierBulkFinancingApprovalListTable;
